import { useState, useCallback, useMemo } from 'react';
import {
	useExportFieldDiary,
	useGetFieldDiaryDashboard,
	useGetFieldDiaryEntries,
} from '@practium/features/api';
import type { FieldDiaryExportFormat } from '@practium/features/api';
import type { FieldDiaryStats } from '../types';
import {
	MONTH_NAMES,
	buildCalendarDays,
	buildEntriesByDay,
	buildFieldDiaryStats,
	downloadBlob,
	toFieldDiaryEntries,
	toMonthKey,
} from '../utils';
import { useFieldDiaryNotes } from './useFieldDiaryNotes';

export interface UseFieldDiaryProps {
	propId: string;
	forBinding?: string;
}

const today = new Date();

export const useFieldDiary = ({ propId, forBinding }: UseFieldDiaryProps) => {
	const [currentDate, setCurrentDate] = useState<Date>(
		new Date(today.getFullYear(), today.getMonth(), 1)
	);
	const [selectedDay, setSelectedDay] = useState<number | null>(null);

	const year = currentDate.getFullYear();
	const month = currentDate.getMonth() + 1;
	const monthKey = toMonthKey(year, month);

	const isFutureMonth = useMemo(() => {
		const now = new Date();
		return year > now.getFullYear() || (year === now.getFullYear() && month > now.getMonth() + 1);
	}, [year, month]);

	const canGoNext = !isFutureMonth;

	const goToPrevMonth = useCallback(() => {
		setCurrentDate((prev) => new Date(prev.getFullYear(), prev.getMonth() - 1, 1));
		setSelectedDay(null);
	}, []);

	const goToNextMonth = useCallback(() => {
		if (!canGoNext) return;
		setCurrentDate((prev) => new Date(prev.getFullYear(), prev.getMonth() + 1, 1));
		setSelectedDay(null);
	}, [canGoNext]);

	const dashboard = useGetFieldDiaryDashboard({ propId, forBinding, month: monthKey });
	const entriesQuery = useGetFieldDiaryEntries({
		propId,
		forBinding,
		month: monthKey,
		enabled: dashboard.isFetched,
	});
	const { saveNote, deleteNote, isDeleting } = useFieldDiaryNotes({ propId, forBinding });
	const { mutateAsync: requestExport, isPending: isExporting } = useExportFieldDiary({
		forBinding,
	});

	const entries = useMemo(() => toFieldDiaryEntries(entriesQuery.data), [entriesQuery.data]);

	const stats: FieldDiaryStats = useMemo(
		() => buildFieldDiaryStats(dashboard.data),
		[dashboard.data]
	);

	const entriesByDay = useMemo(() => buildEntriesByDay(entries), [entries]);

	const monthLabel = useMemo(
		() => `${MONTH_NAMES[currentDate.getMonth()]} ${year}`,
		[currentDate, year]
	);

	const calendarDays = useMemo(() => buildCalendarDays(year, month), [year, month]);

	const isToday = useCallback(
		(day: number) => {
			const now = new Date();
			return day === now.getDate() && month === now.getMonth() + 1 && year === now.getFullYear();
		},
		[year, month]
	);

	const exportDiary = useCallback(
		async (format: FieldDiaryExportFormat) => {
			const file = await requestExport({ propId, month: monthKey, format });
			downloadBlob(file, `diario-de-campo-${monthKey}.${format}`);
		},
		[requestExport, propId, monthKey]
	);

	return {
		currentDate,
		year,
		month,
		monthLabel,
		canGoNext,
		goToPrevMonth,
		goToNextMonth,
		entries,
		entriesByDay,
		stats,
		isSummaryLoading: dashboard.isPending,
		isEntriesLoading: entriesQuery.isPending,
		loadError: dashboard.error ?? entriesQuery.error,
		calendarDays,
		selectedDay,
		setSelectedDay,
		isToday,
		saveNote,
		deleteNote,
		isDeleting,
		exportDiary,
		isExporting,
	};
};
